import React, { MouseEvent } from "react";
import { Stack } from "web/react/emo/shared/components/stack";
import { Text } from "web/react/emo/text";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import * as styles from "./storefront-description.css";

export interface StorefrontDescriptionProps {
    description?: string | null;
    retailer?: string;
    beforeOnClick?: () => void;
    displayAsPreview?: boolean;
    parentArea: "header" | "modal" | "product_details" | "compare_prices";
    retailerNetwork: string | null;
}

function StorefrontDescriptionFull({
    description,
}: Pick<StorefrontDescriptionProps, "description">): JSX.Element | null {
    if (!description) {
        return null;
    }

    return (
        <Text textStyle="body-s" dataTestId="storefront-description-text" noSpacing>
            <div dangerouslySetInnerHTML={{ __html: description }} />
        </Text>
    );
}

function StorefrontDescriptionPreview({
    description,
    beforeOnClick,
    parentArea,
    retailerNetwork,
}: Pick<
    StorefrontDescriptionProps,
    "description" | "retailer" | "beforeOnClick" | "parentArea" | "retailerNetwork"
>): JSX.Element {
    const { isMobileViewport } = useDomViewport();

    const moreButtonText = `...${gettext("general.more").toLowerCase()}`;

    const onClick = (e: MouseEvent): void => {
        beforeOnClick?.();
        const target = e.target as HTMLElement;
        if (target.tagName.toLowerCase() === "a") {
            return;
        }
        analytics.event(
            "description",
            "clicked",
            "",
            false,
            {},
            "",
            {},
            {
                parent_area: parentArea,
                retailer_network: retailerNetwork,
            }
        );
    };

    return (
        <div
            onClick={onClick}
            data-testid="storefront-description"
            className={styles.descriptionContainer}
        >
            <Stack direction={"row"} align={"end"}>
                <Text
                    textStyle={{ sm: "body-s", md: "body" }}
                    lineLimit={isMobileViewport ? 3 : 2}
                    dataTestId="storefront-description-text"
                >
                    {description ? (
                        <div dangerouslySetInnerHTML={{ __html: description }} />
                    ) : (
                        gettext("general.learn_more_seller")
                    )}
                </Text>
                <span className={description ? styles.fadeOverlay : styles.noOverlay}>
                    <button
                        data-testid="storefront-description-button"
                        className={styles.moreButton}
                    >
                        <Text textStyle={{ sm: "body-s-bold", md: "body-bold" }}>
                            {moreButtonText}
                        </Text>
                    </button>
                </span>
            </Stack>
        </div>
    );
}

export function StorefrontDescription({
    description,
    retailer,
    beforeOnClick,
    displayAsPreview = false,
    parentArea,
    retailerNetwork,
}: StorefrontDescriptionProps): JSX.Element {
    // only remove outer <p> tags, to ensure better compatibility with <Text/>
    // and respect inner HTML in description
    const trimmedDescription = description?.replace(/^<p[^>]*>([\s\S]*)<\/p>$/i, "$1");

    if (displayAsPreview) {
        return (
            <StorefrontDescriptionPreview
                description={trimmedDescription}
                retailer={retailer}
                beforeOnClick={beforeOnClick}
                parentArea={parentArea}
                retailerNetwork={retailerNetwork}
            />
        );
    }

    return <StorefrontDescriptionFull description={trimmedDescription} />;
}
